import { isValidElement, memo } from 'react';
import { ComponentMemo } from '@headless-workspace/typings';
import { StringHelpers } from '@headless-workspace/utils';
import { GlowTypographyProps, typographyStyle } from './GlowTypographyProps';

/**
 * Represents a GlowTypography component.
 *
 * @param className - The additional CSS class name for the component.
 * @param fontFamily - The font family variant for the component.
 * @param fontSize - The font size variant for the component.
 * @param fontWeight - The font weight variant for the component.
 * @param textDecoration - The text decoration variant for the component.
 * @param TagName - The HTML tag name to be used for the component.
 * @param color - The color variant for the component.
 * @param text - The text content of the component.
 * @param innerHTML - The HTML content of the component.
 * @param dataTestId - The data-testid attribute for the component.
 * @param role - The role attribute for the component.
 * @param structuredData - Optional structured data for SEO. Can contain the following:
 * @param structuredData.itemScope - Indicates that defines the scope of associated metadata.
 * @param structuredData.itemProp - Used to add properties to an item.
 * @param structuredData.itemType - Specifies the URL of the vocabulary that will be used to define itemprop's (item properties) in the data structure.
 * @param structuredData.content - The content of the structured data.
 * @param props - Additional props to be spread to the underlying HTML element.
 * @returns The rendered GlowTypography component.
 */
export const GlowTypography: ComponentMemo<GlowTypographyProps> = memo(
    ({
        className,
        fontFamily,
        fontSize,
        fontWeight,
        textDecoration,
        TagName = 'p',
        color = 'primary',
        text,
        innerHTML,
        dataTestId,
        role,
        structuredData,
        ...props
    }) => {
        if (!text && !innerHTML) {
            return null;
        }

        const classNames = typographyStyle({ fontFamily, fontSize, textDecoration, fontWeight, className, color });

        if (typeof innerHTML !== 'string' && isValidElement(innerHTML)) {
            // @ts-expect-error innerHtml is a ReactNode, in this case it should always have a rawHtml prop
            const htmlString = innerHTML.props?.rawHtml?.content ?? innerHTML;

            if (typeof htmlString === 'string') {
                return (
                    <TagName
                        className={classNames}
                        dangerouslySetInnerHTML={{ __html: StringHelpers.removeOuterHTMLTag(htmlString) }}
                        data-testid={dataTestId}
                        role={role}
                        itemProp={structuredData?.itemProp}
                        itemScope={structuredData?.itemScope}
                        itemType={structuredData?.itemType}
                        content={structuredData?.content}
                        {...props}
                    />
                );
            }
        }
        return (
            <TagName
                className={classNames}
                data-testid={dataTestId}
                role={role}
                itemProp={structuredData?.itemProp}
                itemScope={structuredData?.itemScope}
                itemType={structuredData?.itemType}
                content={structuredData?.content}
                {...props}
            >
                {text || innerHTML}
            </TagName>
        );
    },
);

GlowTypography.displayName = 'GlowTypography';
